import { Head, Link, router, usePage } from "@inertiajs/react"
import { SquarePen } from "lucide-react"

import { HpiDataTable } from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import {
  companyResourcesPath,
  companySignaturePath,
  companySignaturesPath,
  editCompanySignaturePath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface Signature {
  id: number
  name: string
  status: number
}

interface Props {
  signatures: Signature[]
}

/* ── Columns ───────────────────────────────────────────── */

const baseColumns: HpiColumn<Signature>[] = [
  {
    header: "Name",
    render: (row) => (
      <div className="text-hpi-active-blue text-sm font-medium">{row.name}</div>
    ),
  },
  {
    header: "Status",
    width: "max-w-[100px]",
    render: (row) => (
      <span
        className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${
          row.status === 0
            ? "bg-green-100 text-green-800"
            : "bg-yellow-100 text-yellow-800"
        }`}
      >
        {row.status === 0 ? "Active" : "Inactive"}
      </span>
    ),
  },
]

const editColumn: HpiColumn<Signature> = {
  header: "",
  width: "max-w-[60px]",
  render: (row) => (
    <Link
      href={editCompanySignaturePath(row.id)}
      className="text-hpi-active-blue text-sm hover:underline"
      onClick={(e) => e.stopPropagation()}
    >
      Edit
    </Link>
  ),
}

/* ── Main Component ────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Company", href: companyResourcesPath() },
  { title: "Signatures", href: companySignaturesPath() },
]

export default function SignaturesIndex({ signatures }: Props) {
  const { auth } = usePage().props
  const isAdmin = auth.user.is_admin

  const actions = isAdmin
    ? [
        {
          label: "Add",
          icon: SquarePen,
          onClick: () => router.visit("/company/signatures/new"),
        },
      ]
    : []

  const columns = isAdmin ? [...baseColumns, editColumn] : baseColumns

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Signatures" />

      <div className="p-6">
        <HpiDataTable<Signature>
          title="Email Signatures"
          actions={actions}
          columns={columns}
          data={signatures}
          keyFn={(row) => row.id}
          rowHref={(row) => companySignaturePath(row.id)}
        />
      </div>
    </AppLayout>
  )
}
